import { TransactionListItem } from "@common/schemas/transaction.schema";
import { sumMonetaryValues } from "@common/helpers/monetary";
import { JournalEntryLineProxyObject } from "./JournalEntryDrawer";
import { getFinancialJournalEntryRowIndex } from "./journalEntryTransactionLines";

export interface CollapseJournalEntryLinesResult {
    displayLines: JournalEntryLineProxyObject[];
    displayToSourceIndices: number[][];
}

function normalizeCounterpartyId(
    counterpartyId: string | null | undefined,
): string {
    return counterpartyId ?? "";
}

function shareCollapseKey(
    left: JournalEntryLineProxyObject,
    right: JournalEntryLineProxyObject,
): boolean {
    if (!left.accountId || left.accountId !== right.accountId) {
        return false;
    }
    return (
        normalizeCounterpartyId(left.counterpartyId) ===
        normalizeCounterpartyId(right.counterpartyId)
    );
}

function uniqueClassIds(lines: JournalEntryLineProxyObject[]): string[] {
    const seen = new Set<string>();
    const classIds: string[] = [];
    for (const line of lines) {
        for (const classId of line.classIds ?? []) {
            if (seen.has(classId)) {
                continue;
            }
            seen.add(classId);
            classIds.push(classId);
        }
    }
    return classIds;
}

function sumNullableAmounts(
    amounts: Array<number | null | undefined>,
): number | null {
    const present = amounts.filter(
        (amount): amount is number => amount != null,
    );
    if (present.length === 0) {
        return null;
    }
    return sumMonetaryValues(present);
}

function mergeCollapsedLines(
    lines: JournalEntryLineProxyObject[],
): JournalEntryLineProxyObject {
    const [first, ...rest] = lines;
    return {
        ...first,
        debitAmount: sumNullableAmounts([
            first.debitAmount,
            ...rest.map((line) => line.debitAmount),
        ]),
        creditAmount: sumNullableAmounts([
            first.creditAmount,
            ...rest.map((line) => line.creditAmount),
        ]),
        classIds: uniqueClassIds(lines),
    };
}

export function getUneditableFinancialRowIndices(
    lines: Array<{ transactionId?: number | null }>,
    transactions: Array<Pick<TransactionListItem, "id" | "amount">>,
): Set<number> {
    const indices = new Set<number>();
    for (const transaction of transactions) {
        const financialRow = getFinancialJournalEntryRowIndex(
            lines,
            transaction,
        );
        if (financialRow !== undefined) {
            indices.add(financialRow);
        }
    }
    return indices;
}

export function collapseUneditableJournalEntryLines({
    lines,
    uneditableRowIndices,
}: {
    lines: JournalEntryLineProxyObject[];
    uneditableRowIndices: Set<number>;
}): CollapseJournalEntryLinesResult {
    if (uneditableRowIndices.size === 0) {
        return {
            displayLines: lines,
            displayToSourceIndices: lines.map((_, index) => [index]),
        };
    }

    const consumed = new Set<number>();
    const collapsedRows: Array<{
        line: JournalEntryLineProxyObject;
        sources: number[];
    }> = [];
    const remainingRows: Array<{
        line: JournalEntryLineProxyObject;
        sources: number[];
    }> = [];

    for (let index = 0; index < lines.length; index++) {
        if (consumed.has(index)) {
            continue;
        }
        const line = lines[index];
        const canCollapse =
            uneditableRowIndices.has(index) && line.accountId.length > 0;

        if (!canCollapse) {
            remainingRows.push({ line, sources: [index] });
            continue;
        }

        const members = [index];
        for (let next = index + 1; next < lines.length; next++) {
            if (
                consumed.has(next) ||
                !uneditableRowIndices.has(next) ||
                lines[next].accountId.length === 0
            ) {
                continue;
            }
            if (shareCollapseKey(line, lines[next])) {
                members.push(next);
                consumed.add(next);
            }
        }

        const displayRow = {
            line:
                members.length === 1
                    ? line
                    : mergeCollapsedLines(
                          members.map((member) => lines[member]),
                      ),
            sources: members,
        };
        if (members.length > 1) {
            collapsedRows.push(displayRow);
        } else {
            remainingRows.push(displayRow);
        }
    }

    const orderedRows = [...collapsedRows, ...remainingRows];
    return {
        displayLines: orderedRows.map((row) => row.line),
        displayToSourceIndices: orderedRows.map((row) => row.sources),
    };
}

export function applyDisplayLinesToSource({
    sourceLines,
    displayLines,
    displayToSourceIndices,
}: {
    sourceLines: JournalEntryLineProxyObject[];
    displayLines: JournalEntryLineProxyObject[];
    displayToSourceIndices: number[][];
}): JournalEntryLineProxyObject[] {
    const nextLines = sourceLines.map((line) => ({ ...line }));

    for (
        let displayIndex = 0;
        displayIndex < displayLines.length;
        displayIndex++
    ) {
        const sourceIndices = displayToSourceIndices[displayIndex];
        const displayLine = displayLines[displayIndex];
        if (!sourceIndices || sourceIndices.length === 0) {
            nextLines.push({ ...displayLine });
            continue;
        }
        if (sourceIndices.length !== 1) {
            continue;
        }
        const sourceIndex = sourceIndices[0];
        const sourceLine = nextLines[sourceIndex];
        if (!sourceLine) {
            continue;
        }
        nextLines[sourceIndex] = {
            ...sourceLine,
            ...displayLine,
            id: sourceLine.id,
            transactionId: sourceLine.transactionId,
        };
    }

    return nextLines;
}
